import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { LoginCredentials } from '../../types';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { EyeIcon, EyeOffIcon } from 'lucide-react'; 

export const LoginForm: React.FC = () => {
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false); 

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginCredentials>({
    mode: 'onChange',
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  const navigate = useNavigate();

  const onSubmit = async (data: LoginCredentials) => {
    try {
      setIsLoading(true);
      const result: any = await login(data);
      toast.success(result?.message || 'Login successful!');
      console.log(result, 'Login Result');
    } catch (error: any) {
      toast.error(error?.message || 'Login failed');
      setLoginError(
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Email Field */}
      <Input
        {...register('email', {
          required: 'Email address is required',
        })}
        label="Email Address"
        type="email"
        placeholder="Enter your email address"
        error={hasSubmitted ? errors.email?.message : undefined}
        leftIcon={
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
            />
          </svg>
        }
        variant="outlined"
        size="lg"
      />

      <div className="relative">
        <Input
          {...register('password', {
            required: 'Password is required',
          })}
          label="Password"
          type={showPassword ? 'text' : 'password'} 
          placeholder="Enter your password"
          error={hasSubmitted ? errors.password?.message : undefined}
          leftIcon={
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          }
          variant="outlined"
          size="lg"
        />

        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-4 top-[45px] text-gray-500 hover:text-gray-700 focus:outline-none"
        >
          {showPassword ? (
            <EyeOffIcon className="w-5 h-5" />
          ) : (
            <EyeIcon className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Remember Me & Forgot Password */}
      <div className="flex items-center justify-between">
        <label className="flex items-center">
          <input
            {...register('rememberMe')}
            type="checkbox"
            className="h-4 w-4 text-agentic-primary-blue focus:ring-agentic-primary-blue border-agentic-gray-300 rounded"
          />
          <span className="ml-2 text-sm text-agentic-text-secondary">
            Remember me
          </span>
        </label>

        <button
          type="button"
          onClick={() => navigate('/forgot-password')}
          className="text-sm text-agentic-primary-blue hover:text-agentic-primary-blue-dark font-medium transition-colors duration-200"
        >
          Forgot password?
        </button>
      </div>

      {/* Login Error */}
      {loginError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{loginError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        variant="primary"
        size="lg"
        loading={isLoading}
        disabled={isLoading}
        className="w-full"
      >
        {isLoading ? 'Signing in...' : 'Sign in to your account'}
      </Button>

      {/* Additional Info */}
      <div className="text-center">
        <p className="text-sm text-agentic-text-secondary">
          Don't have an account?{' '}
          <button
            type="button"
            className="text-agentic-primary-blue hover:text-agentic-primary-blue-dark font-medium transition-colors duration-200"
          >
            Contact your administrator
          </button>
        </p>
      </div>
    </form>
  );
};
